/**
 * Os tipos do wizard de criação de página, num lugar só.
 *
 * Cada passo mora num arquivo e todos falam do mesmo formulário — com o tipo
 * declarado dentro de cada um, a primeira mudança de campo já deixaria dois
 * passos discordando sobre a forma do mesmo dado.
 */

export interface Subcategory {
  id: number
  key: string
  label: string
  /** O negócio atende (serviço) ou vende (produto)? Vem do catálogo. */
  kind: 'service' | 'product'
  /** Webp em /explore/taxonomia — o card do Figma. */
  image_path: string | null
}

export interface Category {
  id: number
  key: string
  label: string
  image_path: string | null
  subcategories: Subcategory[]
}

export interface Audience {
  key: string
  label: string
}

export interface Photo {
  id: number
  url: string
}

/** Um produto ou serviço cadastrado pelo criador. */
export interface CatalogItem {
  id: number
  kind: 'servico' | 'produto'
  name: string
  description: string
  /** Em reais. `null` é "sob consulta" — zero seria lido como grátis. */
  price: number | null
  unit: string
  position: number
  photos: Photo[]
}

/**
 * Dias e faixas de atendimento. Mesmo shape que a escola já grava, para a
 * página pública desenhar os dois com o mesmo componente.
 */
export interface OperatingHours {
  days: number[]
  morning: { start: string; end: string } | null
  afternoon: { start: string; end: string } | null
}

/** O rascunho como o servidor devolve. */
export interface DraftPage {
  id: number
  name: string
  address: string
  city: string
  state: string
  postal_code: string
  latitude: number | null
  longitude: number | null
  whatsapp: string
  website: string
  instagram: string
  tiktok: string
  youtube: string
  audiences: string[]
  years_experience: string
  brief_description: string
  extra_links: string[]
  operating_hours: OperatingHours | null
  subcategory_ids: number[]
  items: CatalogItem[]
  /** A capa — o rosto do negócio no card do diretório. */
  cover: Photo | null
  /** A galeria da página, que não é a das fotos do catálogo. */
  photos: Photo[]
  /**
   * Os links dos vídeos do YouTube, sempre com 3 posições (vazias inclusive).
   * O servidor guarda o ID e devolve link — ver PagesController#video_links.
   * A ORDEM é a identidade: o primeiro é o que vai para o destaque.
   */
  videos: string[]
}

/** O estado do formulário no cliente. */
export interface WizardForm {
  subcategory_ids: number[]
  name: string
  address: string
  city: string
  state: string
  postal_code: string
  latitude: number | null
  longitude: number | null
  whatsapp: string
  website: string
  instagram: string
  tiktok: string
  youtube: string
  operating_hours: OperatingHours
  audiences: string[]
  years_experience: string
  extra_links: string[]
  brief_description: string
  videos: string[]
  plan: 'mensal' | 'semestral' | 'anual'
  coupon_code: string
}

export type SetField = <K extends keyof WizardForm>(
  key: K,
  value: WizardForm[K],
) => void

export type FormErrors = Partial<Record<string, string | string[]>>

/** Rascunho pela metade, listado para quem abre /nova-pagina do zero. */
export interface DraftRow {
  id: number
  name: string | null
  step: number
  updated_at: string
  href: string
}

export const EMPTY_HOURS: OperatingHours = {
  days: [],
  morning: null,
  afternoon: null,
}
